import { useEffect, useState } from "react";
import { ShippingAddress } from "../../types";

import { styled, useMediaQuery, useTheme } from "@mui/material";

import { getFullAddressWithNames } from "@/utils/StringUtils";
import { AddCircleOutlineIcon } from "@brandclub/common-ui";
import { useAppSelector } from "../../../../redux/hooks";
import {
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "../../../AppNavigation/constants";
import StoreRadio from "../../../StoreComponents/StoreRadio";
import AddressForm from "../CheckoutV2/CheckoutForm/components/AddressForm";
import ButtonContainer from "./components/Buttons/ButtonContainer";
import SaveButton from "./components/Buttons/SaveButton";
import ClearButton from "./components/Buttons/ClearButton";
import { Address } from "@/redux/types";
import { ButtonLoadingEllipsis } from "@/components/StoreComponents/StoreButton";

const Container = styled("div")(({ theme }) => ({
  marginBottom: 20,
  ".existing": {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 6,
    },
    ".info_row": {
      cursor: "pointer",
      display: "flex",
      alignItems: "flex-start",
      fontSize: 14,
      fontWeight: 500,
      ".icon": {
        width: 30,
        height: 30,
        objectFit: "contain",
        marginRight: 10,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          width: 20,
          height: 20,

          marginRight: 8,
        },
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          width: 20,
          height: 20,

          marginRight: 8,
        },
      },
      ".add_new_icon": {
        lineHeight: 0,
        marginRight: 10,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: 22,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          fontSize: 17,
        },
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 17,
        },
      },
    },
  },

  ".actions": {
    marginTop: 30,
    display: "flex",
    gap: 20,
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));

export const isAddressEqual = (
  address1?: Address | ShippingAddress | null,
  address2?: Address | ShippingAddress | null
) => {
  if (!address1 || !address2) {
    return false;
  }

  return (
    (address1?.id && address2?.id && address1.id === address2.id) ||
    (address1?.address1 === address2?.address1 &&
      address1?.address2 === address2?.address2 &&
      address1?.city === address2?.city &&
      address1?.state === address2?.state &&
      address1?.zipCode === address2?.zipCode &&
      address1?.firstName === address2?.firstName &&
      address1?.lastName === address2?.lastName)
  );
};

const CheckoutEditShippingForm = ({
  existingAddress,
  handleSaveShippingAddress,
  availableAddresses,
  onChange,
  onCancel,
  disableCancel = false,
  loading = false,
}: {
  availableAddresses?: ShippingAddress[];
  existingAddress?: ShippingAddress;
  handleSaveShippingAddress: (address: Partial<ShippingAddress>) => void;
  onChange?: (address: Partial<ShippingAddress>) => void;
  onCancel?: () => void;
  disableCancel?: boolean;
  loading?: boolean;
}) => {
  const addresses = availableAddresses;
  const phoneNumberFromProfile = useAppSelector(
    (state) => state.userProfile?.phoneNumber
  );
  const mobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  const [address1, setAddress1] = useState(existingAddress?.address1 || "");
  const [address2, setAddress2] = useState(existingAddress?.address2 || "");
  const [city, setCity] = useState(existingAddress?.city || "");
  const [state, setState] = useState(existingAddress?.state || "");
  const [zipCode, setZipCode] = useState(existingAddress?.zipCode || "");
  const [firstName, setFirstName] = useState(existingAddress?.firstName || "");
  const [lastName, setLastName] = useState(existingAddress?.lastName || "");

  const [addNewOpen, setAddNewOpen] = useState(false);

  const theme = useTheme();

  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(-1);

  useEffect(() => {
    if (
      existingAddress?.extendedAttributes?.isSelected &&
      selectedAddressIndex === -1
    ) {
      const indexOfMatchingAddress =
        addresses?.findIndex((address) =>
          isAddressEqual(address, existingAddress)
        ) ?? -1;
      if (indexOfMatchingAddress !== -1) {
        setSelectedAddressIndex(indexOfMatchingAddress);
      }
    }
  }, [addresses, existingAddress, selectedAddressIndex]);

  const selectedAddress = addresses?.[selectedAddressIndex];

  useEffect(() => {
    if (!addresses?.length) {
      setAddNewOpen(true);
    }
  }, [addresses?.length]);

  const clearInput = () => {
    if (addNewOpen) {
      setAddress1("");
      setAddress2("");
      setCity("");
      setState("");
      setZipCode("");
      setFirstName("");
      setLastName("");
    } else {
      onCancel?.();
    }
  };

  useEffect(() => {
    if (onChange) {
      if (selectedAddressIndex >= 0) {
        const address = addresses?.[selectedAddressIndex];
        if (address) {
          onChange(address);
        }
      } else {
        onChange({
          address1,
          address2,
          city,
          state,
          zipCode,
          firstName,
          lastName,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address1,
    address2,
    city,
    state,
    zipCode,
    firstName,
    lastName,
    selectedAddressIndex,
  ]);

  const canSubmit =
    (selectedAddressIndex >= 0 ||
      (address1 && city && state && zipCode && firstName && lastName)) &&
    // and it does not match the current address
    !isAddressEqual(selectedAddress, existingAddress);

  return (
    <Container>
      {addresses?.length ? (
        <div className="existing">
          {[...addresses]
            .sort((a, b) => {
              // Show selected address first
              if (selectedAddressIndex >= 0) {
                if (isAddressEqual(a, addresses[selectedAddressIndex]))
                  return -1;
                if (isAddressEqual(b, addresses[selectedAddressIndex]))
                  return 1;
              }
              return 0;
            })
            .map((address, i) => {
              const addressDisplay = getFullAddressWithNames(address);
              const originalIndex = addresses.findIndex((addr) =>
                isAddressEqual(addr, address)
              );
              const selected = selectedAddressIndex === originalIndex;

              return (
                <button
                  className="info_row"
                  key={address.id}
                  onClick={() => {
                    setSelectedAddressIndex(originalIndex);
                    setAddNewOpen(false);
                  }}
                  disabled={loading}
                  style={{
                    fontWeight: selected ? 600 : 500,
                    border: "none",
                    background: "transparent",
                    textAlign: "left",
                    padding: 0,
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                  aria-pressed={selected}
                  aria-label={`Select shipping address: ${addressDisplay}`}
                >
                  <StoreRadio
                    key={address.id}
                    style={{ marginRight: 10, opacity: loading ? 0.5 : 1 }}
                    disabled={loading}
                    checked={selected}
                    disableRipple
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{ "aria-hidden": true }}
                  />
                  {addressDisplay}
                </button>
              );
            })}
          {addNewOpen ? null : (
            <button
              className="info_row"
              onClick={() => {
                setSelectedAddressIndex(-1);
                setAddNewOpen(true);
              }}
              style={{
                border: "none",
                background: "transparent",
                textAlign: "left",
                padding: 0,
                width: "100%",
                display: "flex",
                alignItems: "center",
              }}
              aria-label="Add new shipping address"
              disabled={loading}
            >
              <AddCircleOutlineIcon className="add_new_icon" />
              Add new shipping address
            </button>
          )}
        </div>
      ) : null}

      {addNewOpen ? (
        <AddressForm
          disabled={loading}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          address1={address1}
          setAddress1={setAddress1}
          address2={address2}
          setAddress2={setAddress2}
          city={city}
          setCity={setCity}
          state={state}
          setState={setState}
          zipCode={zipCode}
          setZipCode={setZipCode}
        />
      ) : null}

      <ButtonContainer
        sx={{
          marginTop: "35px",
          [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
            marginTop: "20px",
          },
          alignSelf: "flex-end",
          justifyContent: "space-between",
        }}
      >
        {mobile ? null : (
          <ClearButton onClick={clearInput} disabled={disableCancel || loading}>
            Cancel
          </ClearButton>
        )}
        <SaveButton
          disabled={!canSubmit || loading}
          onClick={() => {
            if (selectedAddress) {
              handleSaveShippingAddress({
                address1: selectedAddress.address1,
                address2: selectedAddress.address2,
                city: selectedAddress.city,
                state: selectedAddress.state,
                zipCode: selectedAddress.zipCode,
                firstName: selectedAddress.firstName,
                lastName: selectedAddress.lastName,
                phoneNumber: phoneNumberFromProfile as string,
              });
            } else {
              handleSaveShippingAddress({
                address1,
                address2,
                city,
                state,
                zipCode,
                firstName,
                lastName,
                phoneNumber: phoneNumberFromProfile as string,
              });
            }
          }}
        >
          {loading ? (
            <ButtonLoadingEllipsis
              baseFontSize={16}
              customColor={theme.palette.primary.main}
              style={{ opacity: 0.5, transform: "translateY(-1px)" }}
            />
          ) : (
            "Save"
          )}
        </SaveButton>
      </ButtonContainer>
    </Container>
  );
};

export default CheckoutEditShippingForm;
